import {
	CastingTimeUnit,
	DistanceType,
	DurationType,
	DurationUnit,
	RangeType,
	Spell,
	SpellDistance,
	SpellDuration,
	SpellRange,
	SpellTime,
} from '../../../generated/types';
import {humanEnumeration, wordByCount} from '../../../components/utils';
import {SpellSchools} from '../../../components/enums';
import {composeDescription} from '../../../lib/seo/text';
import type {OgCardData} from '../../../lib/seo/site';

/*
 * Описание заклинания одной фразой для поисковиков:
 * «Заклинание 1-го круга Волшебная стрела школы Эвокация сотворяется за Действие с дистанцией 120 футов,
 * используя Словесные и Жестовые Компоненты, и действует мгновенно».
 * Форматтеры карточки (toSpellTime, toSpellRange…) пишут в именительном падеже для строк «Время: …»,
 * поэтому для связного текста — свои.
 */

function castingTime(time: SpellTime): string {
	const count = time.number || 1;
	switch (time.unit) {
		case CastingTimeUnit.Action:
			return count > 1 ? `за ${wordByCount(count, ['Действие', 'Действия', 'Действий'], true)}` : 'за Действие';
		case CastingTimeUnit.Bonus:
			return 'за Бонусное действие';
		case CastingTimeUnit.Reaction:
			return 'за Ответное действие';
		case CastingTimeUnit.Minute:
			return `за ${wordByCount(count, ['минуту', 'минуты', 'минут'], true)}`;
		case CastingTimeUnit.Hour:
			return `за ${wordByCount(count, ['час', 'часа', 'часов'], true)}`;
	}
	return '';
}

function distance(value?: SpellDistance | null): string {
	if (!value?.amount) return '';
	if (value.type === DistanceType.Feet) return wordByCount(value.amount, ['фут', 'фута', 'футов'], true);
	if (value.type === DistanceType.Miles) return wordByCount(value.amount, ['миля', 'мили', 'миль'], true);
	return '';
}

function areaFigure(range: SpellRange): string {
	const amount = range.distance?.amount;
	switch (range.type) {
		case RangeType.cube:
			return `${amount}-футовый Куб`;
		case RangeType.cone:
			return `${amount}-футовый Конус`;
		case RangeType.line:
			return `${amount}-футовая Линия`;
		case RangeType.emanation:
			return `${amount}-футовая Эманация`;
		case RangeType.sphere:
			return `Сфера радиусом ${distance(range.distance)}`;
		case RangeType.radius:
			return `Радиус ${distance(range.distance)}`;
		case RangeType.hemisphere:
			return `Полусфера радиусом ${distance(range.distance)}`;
	}
	return '';
}

function rangePhrase(range?: SpellRange | null): string {
	if (!range) return '';
	const figure = areaFigure(range);
	if (figure) return `на себя (область — ${figure})`;
	if (range.type === RangeType.special) return 'с особой дистанцией';
	switch (range.distance?.type) {
		case DistanceType.Feet:
		case DistanceType.Miles:
			return `с дистанцией ${distance(range.distance)}`;
		case DistanceType.Self:
			return 'на себя';
		case DistanceType.Touch:
			return 'касанием';
		case DistanceType.Sight:
			return 'на цель в поле зрения';
		case DistanceType.Unlimited:
			return 'с неограниченной дистанцией';
	}
	return '';
}

const COMPONENTS = {
	v: ['Словесный', 'Словесные'],
	s: ['Жестовый', 'Жестовые'],
	m: ['Материальный', 'Материальные'],
} as const;

function componentsPhrase(components?: Spell['components'] | null): string {
	const keys = (['v', 's', 'm'] as const).filter((key) => components?.[key]);
	if (!keys.length) return '';
	if (keys.length === 1) return `${COMPONENTS[keys[0]][0]} Компонент`;
	return `${humanEnumeration(keys.map((key) => COMPONENTS[key][1]))} Компоненты`;
}

/** «1 минуту», «до 10 минут» — винительный падеж после «длится» и родительный после «до» */
function amountPhrase(amount: number, units: DurationUnit, upTo: boolean): string {
	const forms: Record<DurationUnit, string[]> = {
		[DurationUnit.Minute]: upTo ? ['минуты', 'минут', 'минут'] : ['минуту', 'минуты', 'минут'],
		[DurationUnit.Hour]: upTo ? ['часа', 'часов', 'часов'] : ['час', 'часа', 'часов'],
		[DurationUnit.Day]: upTo ? ['дня', 'дней', 'дней'] : ['день', 'дня', 'дней'],
		[DurationUnit.Round]: upTo ? ['раунда', 'раундов', 'раундов'] : ['раунд', 'раунда', 'раундов'],
	};
	return `${upTo ? 'до ' : ''}${wordByCount(amount, forms[units], true)}`;
}

function durationPhrase(duration: SpellDuration): string {
	switch (duration.type) {
		case DurationType.Instant:
			return 'действует мгновенно';
		case DurationType.Special:
			return 'имеет особую длительность';
		case DurationType.Permanent: {
			const ends = (duration.ends || [])
				.map((end) => (end === 'dispel' ? 'не будет рассеяно' : end === 'trigger' ? 'не сработает' : ''))
				.filter(Boolean);
			return ends.length ? `действует, пока ${ends.join(' или ')}` : 'действует постоянно';
		}
		case DurationType.Timed: {
			const upTo = !!(duration.upTo || duration.concentration);
			const amount = duration.amount && duration.units ? amountPhrase(duration.amount, duration.units, upTo) : '';
			if (duration.concentration) return `требует Концентрации${amount ? ` и длится ${amount}` : ''}`;
			return amount ? `длится ${amount}` : '';
		}
	}
	return '';
}

export function spellSchoolText(spell: Pick<Spell, 'school'>): string {
	return SpellSchools[spell.school] || spell.school;
}

export function spellLevelText(spell: Pick<Spell, 'level'>): string {
	return spell.level ? `${spell.level}-й круг` : 'Фокус';
}

export function spellDescription(spell: Spell): string {
	const school = spellSchoolText(spell);
	let sentence = spell.level
		? `Заклинание ${spell.level}-го круга ${spell.title} школы ${school}`
		: `Фокус ${spell.title} школы ${school}`;
	if (spell.ritual) sentence += ' (ритуал)';

	const time = humanEnumeration((spell.time || []).map(castingTime).filter(Boolean), 'или');
	const range = rangePhrase(spell.range);
	if (time) sentence += ` сотворяется ${time}`;
	if (range) sentence += ` ${range}`;

	const components = componentsPhrase(spell.components);
	const duration = humanEnumeration((spell.duration || []).map(durationPhrase).filter(Boolean), 'или');
	if (components) sentence += `, используя ${components}`;
	if (duration) sentence += `${components ? ',' : ''} и ${duration}`;
	sentence += '.';

	const classes = (spell.classes || []).map((heroClass) => heroClass.title).filter(Boolean);
	if (classes.length) sentence += ` Классы: ${humanEnumeration(classes)}.`;

	return composeDescription(sentence, spell.description);
}

export function spellOgCard(spell: Spell): OgCardData {
	const parts = [spellLevelText(spell), spellSchoolText(spell)];
	if (spell.ritual) parts.push('ритуал');
	if (spell.duration?.some((duration) => duration.concentration)) parts.push('концентрация');
	return {
		label: spell.level ? 'Заклинание' : 'Фокус',
		title: spell.title,
		originalTitle: spell.originalTitle,
		subtitle: parts.join(' · '),
	};
}
